import { Head, Link, useForm } from "@inertiajs/react"
import {
  Activity,
  AlertCircle,
  CheckCircle,
  Clock,
  DollarSign,
  Plus,
  TrendingUp,
  Zap,
} from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import AppLayout from "@/layouts/app-layout"
import { agentRunPath, agentRunsPath, createRunAgentRunsPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface AgentRun {
  id: number
  agent_type: string
  action: string
  status: string
  started_at: string | null
  completed_at: string | null
  duration_seconds: number | null
  total_tokens: number | null
  estimated_cost: number | null
  tool_calls_count: number
  errors_count: number
}

interface Pagination {
  current_page: number
  total_pages: number
  total_count: number
}

interface Filters {
  status: string
  agent_type: string
}

interface Stats {
  total_runs: number
  completed_runs: number
  failed_runs: number
  success_rate: number
  total_cost: number
  total_tokens: number
  most_used_tools: Record<string, number>
  recent_failures: {
    id: number
    action: string
    started_at: string | null
    error_count: number
  }[]
}

interface Props {
  runs: AgentRun[]
  pagination: Pagination
  filters: Filters
  stats: Stats
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Agent Runs", href: agentRunsPath() },
]

const DEFAULT_OPTIONS = [
  {
    value: "ba-frontend",
    label: "Buenos Aires - Frontend",
    prepend:
      "Focus on Buenos Aires-based developers specializing in frontend. Roles: React developer, Vue.js developer, Angular developer. Find ~20 developers and add them and their companies to the database if new.",
  },
  {
    value: "ba-backend",
    label: "Buenos Aires - Backend",
    prepend:
      "Focus on Buenos Aires-based developers specializing in backend. Roles: Node.js developer, Python developer, Java developer. Find ~20 developers and add them and their companies to the database if new.",
  },
  {
    value: "ba-fullstack",
    label: "Buenos Aires - Full Stack",
    prepend:
      "Focus on Buenos Aires-based developers specializing in full stack. Roles: full stack developer, MERN stack, software engineer. Find ~20 developers and add them and their companies to the database if new.",
  },
  {
    value: "cordoba-data",
    label: "Córdoba - Data",
    prepend:
      "Focus on Córdoba-based developers specializing in data. Roles: data engineer, data scientist, machine learning engineer. Find ~20 developers and add them and their companies to the database if new.",
  },
  {
    value: "cordoba-backend",
    label: "Córdoba - Backend",
    prepend:
      "Focus on Córdoba-based developers specializing in backend. Roles: Ruby on Rails developer, Django developer, Spring Boot developer. Find ~20 developers and add them and their companies to the database if new.",
  },
  {
    value: "rosario-frontend",
    label: "Rosario - Frontend",
    prepend:
      "Focus on Rosario-based developers specializing in frontend. Roles: React developer, Next.js developer, TypeScript developer. Find ~20 developers and add them and their companies to the database if new.",
  },
  {
    value: "rosario-devops",
    label: "Rosario - DevOps",
    prepend:
      "Focus on Rosario-based developers specializing in devops. Roles: DevOps engineer, SRE engineer, cloud engineer. Find ~20 developers and add them and their companies to the database if new.",
  },
  {
    value: "tucuman-mobile",
    label: "Tucumán - Mobile",
    prepend:
      "Focus on Tucumán-based developers specializing in mobile. Roles: iOS developer, Android developer, React Native developer. Find ~20 developers and add them and their companies to the database if new.",
  },
  {
    value: "bariloche-backend",
    label: "Bariloche - Backend",
    prepend:
      "Focus on Bariloche-based developers specializing in backend. Roles: Go developer, Rust developer, systems engineer. Find ~20 developers and add them and their companies to the database if new.",
  },
  {
    value: "mendoza-fullstack",
    label: "Mendoza - Full Stack",
    prepend:
      "Focus on Mendoza-based developers specializing in full stack. Roles: full stack developer, software engineer, web developer. Find ~20 developers and add them and their companies to the database if new.",
  },
]

export default function AgentRunsIndex({ runs, pagination, stats }: Props) {
  const [open, setOpen] = useState(false)
  const { data, setData, post, processing } = useForm({
    mode: DEFAULT_OPTIONS[0].value,
    custom_text: "",
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(createRunAgentRunsPath(), {
      onSuccess: () => {
        setOpen(false)
        setData("mode", DEFAULT_OPTIONS[0].value)
        setData("custom_text", "")
      },
    })
  }

  const getStatusBadge = (status: string) => {
    const styles = {
      completed:
        "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300",
      failed: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300",
      running:
        "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300",
      pending:
        "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300",
    }

    const icons = {
      completed: <CheckCircle className="h-3 w-3" />,
      failed: <AlertCircle className="h-3 w-3" />,
      running: <Activity className="h-3 w-3" />,
      pending: <Clock className="h-3 w-3" />,
    }

    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${
          styles[status as keyof typeof styles]
        }`}
      >
        {icons[status as keyof typeof icons]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    )
  }

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return "-"
    return `${seconds.toFixed(2)}s`
  }

  const formatCost = (cost: number | null) => {
    if (!cost) return "-"
    return `$${cost.toFixed(4)}`
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-"
    return new Date(dateString).toLocaleString()
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Agent Runs Dashboard" />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Agent Runs Dashboard</h1>
            <p className="text-muted-foreground">
              Monitor and analyze agent activity and performance
            </p>
          </div>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                New Run
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Start New Scraping Run</DialogTitle>
                <DialogDescription>
                  Choose the scraping mode for this run.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="max-h-[400px] overflow-y-auto pr-2">
                  <RadioGroup
                    value={data.mode}
                    onValueChange={(value) => setData("mode", value)}
                    className="space-y-2"
                  >
                    {DEFAULT_OPTIONS.map((option) => (
                      <div
                        key={option.value}
                        className="flex items-center space-x-2"
                      >
                        <RadioGroupItem
                          value={option.value}
                          id={option.value}
                        />
                        <Label
                          htmlFor={option.value}
                          className="cursor-pointer font-normal"
                        >
                          {option.label}
                        </Label>
                      </div>
                    ))}
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="custom" id="custom" />
                      <Label
                        htmlFor="custom"
                        className="cursor-pointer font-normal"
                      >
                        Custom
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {data.mode === "custom" && (
                  <div className="space-y-2">
                    <Label htmlFor="custom_text">Custom Text</Label>
                    <Input
                      id="custom_text"
                      value={data.custom_text}
                      onChange={(e) => setData("custom_text", e.target.value)}
                      placeholder="Enter custom text..."
                      disabled={processing}
                    />
                  </div>
                )}

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setOpen(false)}
                    disabled={processing}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={processing}>
                    {processing ? "Starting..." : "Start Run"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <div className="bg-card rounded-xl border p-4">
            <div className="flex items-center gap-2">
              <Activity className="text-muted-foreground h-5 w-5" />
              <span className="text-muted-foreground text-sm">Total Runs</span>
            </div>
            <p className="mt-2 text-3xl font-bold">{stats.total_runs}</p>
            <p className="text-muted-foreground mt-1 text-xs">Last 7 days</p>
          </div>

          <div className="bg-card rounded-xl border p-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="text-muted-foreground h-5 w-5" />
              <span className="text-muted-foreground text-sm">
                Success Rate
              </span>
            </div>
            <p className="mt-2 text-3xl font-bold">{stats.success_rate}%</p>
            <p className="text-muted-foreground mt-1 text-xs">
              {stats.completed_runs} completed / {stats.failed_runs} failed
            </p>
          </div>

          <div className="bg-card rounded-xl border p-4">
            <div className="flex items-center gap-2">
              <DollarSign className="text-muted-foreground h-5 w-5" />
              <span className="text-muted-foreground text-sm">Total Cost</span>
            </div>
            <p className="mt-2 text-3xl font-bold">
              ${stats.total_cost.toFixed(4)}
            </p>
            <p className="text-muted-foreground mt-1 text-xs">Last 7 days</p>
          </div>

          <div className="bg-card rounded-xl border p-4">
            <div className="flex items-center gap-2">
              <Zap className="text-muted-foreground h-5 w-5" />
              <span className="text-muted-foreground text-sm">
                Total Tokens
              </span>
            </div>
            <p className="mt-2 text-3xl font-bold">
              {(stats.total_tokens / 1000).toFixed(1)}k
            </p>
            <p className="text-muted-foreground mt-1 text-xs">Last 7 days</p>
          </div>
        </div>

        {Object.keys(stats.most_used_tools).length > 0 && (
          <div className="bg-card rounded-xl border p-4">
            <h3 className="mb-3 font-semibold">Most Used Tools</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.most_used_tools).map(([tool, count]) => (
                <span
                  key={tool}
                  className="bg-primary/10 text-primary rounded-full px-3 py-1 text-sm"
                >
                  {tool}: {count}
                </span>
              ))}
            </div>
          </div>
        )}

        {runs.length === 0 ? (
          <div className="rounded-xl border p-8 text-center">
            <Activity className="text-muted-foreground mx-auto h-12 w-12" />
            <h3 className="mt-4 text-lg font-semibold">No agent runs yet</h3>
            <p className="text-muted-foreground mt-2">
              Agent runs will appear here once jobs start executing.
            </p>
          </div>
        ) : (
          <div className="rounded-xl border">
            <div className="border-b p-4">
              <h2 className="font-semibold">Recent Agent Runs</h2>
            </div>

            <div className="divide-y">
              {runs.map((run) => (
                <Link
                  key={run.id}
                  href={agentRunPath(run.id)}
                  className="hover:bg-muted/50 flex items-center gap-4 px-4 py-3 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{run.action}</span>
                      {getStatusBadge(run.status)}
                      {run.errors_count > 0 && (
                        <span className="text-destructive text-xs">
                          {run.errors_count} error
                          {run.errors_count > 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                    <div className="text-muted-foreground mt-1 flex items-center gap-4 text-sm">
                      <span>{run.agent_type}</span>
                      <span>{formatDate(run.started_at)}</span>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <div className="text-muted-foreground text-xs">
                          Duration
                        </div>
                        <div className="font-medium">
                          {formatDuration(run.duration_seconds)}
                        </div>
                      </div>
                      <div>
                        <div className="text-muted-foreground text-xs">
                          Tokens
                        </div>
                        <div className="font-medium">
                          {run.total_tokens?.toLocaleString() ?? "-"}
                        </div>
                      </div>
                      <div>
                        <div className="text-muted-foreground text-xs">
                          Cost
                        </div>
                        <div className="font-medium">
                          {formatCost(run.estimated_cost)}
                        </div>
                      </div>
                    </div>
                    <div className="text-muted-foreground mt-1 text-xs">
                      {run.tool_calls_count} tool calls
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {pagination.total_pages > 1 && (
              <div className="border-t p-4">
                <div className="text-muted-foreground flex items-center justify-between text-sm">
                  <span>
                    Page {pagination.current_page} of {pagination.total_pages} (
                    {pagination.total_count} total)
                  </span>
                  <div className="flex gap-2">
                    {pagination.current_page > 1 && (
                      <Link
                        href={agentRunsPath({
                          page: pagination.current_page - 1,
                        })}
                        className="hover:text-foreground"
                      >
                        Previous
                      </Link>
                    )}
                    {pagination.current_page < pagination.total_pages && (
                      <Link
                        href={agentRunsPath({
                          page: pagination.current_page + 1,
                        })}
                        className="hover:text-foreground"
                      >
                        Next
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  )
}
